/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderPermission,
  PRIVILEGED_EP_USER_VERIFICATION,
  UserStatus,
  UserSystemRole,
} from '@gh/builder-shared-data';
import {
  FormRootSchema,
  FormRootSchemaConfig,
  FormSchemaFactory,
  FormState, FormStateSchema,
  isRouteForCreationMode,
  requiredValidator,
  toAppFormGroup,
  updateSchemaWithChildren,
  UserContext,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { FormMode, getControlValue, setControlValue } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import { UserDetails, UserIntranetCredentials } from '../../resources/user-details.model';

@Injectable()
export class UserDetailsIntranetFormState extends FormState<UserIntranetCredentials> {
  private user: UserDetails;

  constructor(private route: ActivatedRoute,
              private userContext: UserContext,
              schemaFactory: FormSchemaFactory) {
    super(schemaFactory);
  }

  get invalid(): boolean {
    return isNotNil(requiredValidator(getControlValue(this.group, 'intranetUser')))
      || isNotNil(requiredValidator(getControlValue(this.group, 'intranetPassword')));
  }

  build(credentials: UserIntranetCredentials, user: UserDetails): void {
    this.user = user;

    this.initSchema(credentials);
  }

  markAsRequired(hasRequiredError: boolean): void {
    setControlValue(this.group, 'hasRequiredError', hasRequiredError);
  }

  recalculate(user: UserDetails): void {
    updateSchemaWithChildren(toAppFormGroup(this.group), this.getFormSchemaConfig(this.submitValue, user));
  }

  protected buildSchema(credentials: UserIntranetCredentials, { root }: FormStateSchema): FormRootSchema {
    const { user } = this;

    const mode = isRouteForCreationMode(this.route) ? FormMode.CreateMode
      : (!this.userContext.hasPermission(BuilderPermission.Setup.Manager.UserDetails.EDIT)
      || (user.status === UserStatus.Deleted || user.status === UserStatus.Transferred)
        ? FormMode.ViewMode : FormMode.EditMode);

    return root({
      mode,
      enabled: whenCreateOrEditMode(),
      ...this.getFormSchemaConfig(credentials, user),
    });
  }

  private getFormSchemaConfig(credentials: UserIntranetCredentials, user: UserDetails): FormRootSchemaConfig {
    const isVisible = [UserStatus.Active, UserStatus.Deleted].includes(<any>user.status)
      && user.systemRoles[UserSystemRole.Salesperson] || false;

    return {
      children: {
        intranetUser: {
          visible: isVisible,
          required: credentials.hasRequiredError,
          privileged: user.isIntranetVerified ? PRIVILEGED_EP_USER_VERIFICATION : void 0,
        },
        intranetPassword: {
          visible: isVisible,
          required: credentials.hasRequiredError,
          privileged: user.isIntranetVerified ? PRIVILEGED_EP_USER_VERIFICATION : void 0,
          whenPrivileged: 'hide',
        },
        hasRequiredError: void 0,
      },
      privilegedEntryPoints: user.isIntranetVerified ? [PRIVILEGED_EP_USER_VERIFICATION] : void 0,
    };
  }
}
